"use client";

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

const UserContext =
    createContext(null);

export function UserProvider({
    children,
}) {
    const [
        user,
        setUserState,
    ] = useState(null);

    const [
        isUserLoading,
        setIsUserLoading,
    ] = useState(true);

    const [
        userError,
        setUserError,
    ] = useState("");

    const [
        authChangeVersion,
        setAuthChangeVersion,
    ] = useState(0);

    const requestStartedRef =
        useRef(false);

    const setUser = useCallback(
        (nextUser) => {
            setUserState(
                nextUser || null
            );

            setUserError("");
            setIsUserLoading(false);

            setAuthChangeVersion(
                (currentVersion) =>
                    currentVersion + 1
            );
        },
        []
    );

    const clearUser =
        useCallback(() => {
            setUserState(null);
            setUserError("");
            setIsUserLoading(false);

            setAuthChangeVersion(
                (currentVersion) =>
                    currentVersion + 1
            );
        }, []);

    const refreshUser =
        useCallback(
            async ({
                force = false,
            } = {}) => {
                if (
                    requestStartedRef.current &&
                    !force
                ) {
                    return null;
                }

                requestStartedRef.current =
                    true;

                setIsUserLoading(true);
                setUserError("");

                try {
                    const response =
                        await fetch(
                            "/api/auth/me",
                            {
                                method:
                                    "GET",
                                cache:
                                    "no-store",
                            }
                        );

                    const data =
                        await response
                            .json()
                            .catch(
                                () => null
                            );

                    if (!response.ok) {
                        throw new Error(
                            data?.message ||
                            "Nem sikerült lekérni a felhasználói adatokat."
                        );
                    }

                    const nextUser =
                        data?.user ||
                        null;

                    setUserState(
                        nextUser
                    );

                    return nextUser;
                } catch (error) {
                    setUserState(null);

                    setUserError(
                        error?.message ||
                        "Nem sikerült lekérni a felhasználói adatokat."
                    );

                    return null;
                } finally {
                    setIsUserLoading(
                        false
                    );
                }
            },
            []
        );

    useEffect(() => {
        refreshUser();
    }, [refreshUser]);

    const value = useMemo(() => {
        const isAdmin =
            user
                ?.isAdmin ===
            true;

        const isReceptionist =
            user
                ?.isReceptionist ===
            true;

        return {
            user,

            isLoggedIn:
                Boolean(user),

            isAdmin,
            isReceptionist,

            canAccessMarketing: user?.isMarketing === true,

            canAccessAdmin:
                isAdmin,

            canAccessReception:
                isAdmin ||
                isReceptionist,

            isUserLoading,
            userError,
            authChangeVersion,

            setUser,
            clearUser,
            refreshUser,
        };
    }, [
        user,
        isUserLoading,
        userError,
        authChangeVersion,
        setUser,
        clearUser,
        refreshUser,
    ]);

    return (
        <UserContext.Provider
            value={value}
        >
            {children}
        </UserContext.Provider>
    );
}

export function useUser() {
    const context =
        useContext(UserContext);

    if (!context) {
        throw new Error(
            "A useUser csak UserProvideren belül használható."
        );
    }

    return context;
}